import React, { useEffect, useMemo, useState, useRef } from "react";
import { api, speak } from "@/lib/kielo";
import { useI18n } from "@/i18n";
import { Button } from "@/components/ui/button";
import { Volume2, RefreshCw, Trophy } from "lucide-react";

function shuffle(arr) {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
}

const ROUND = 6;

export default function MatchGame() {
  const { t, lang } = useI18n();
  const [pool, setPool] = useState([]);
  const [pairs, setPairs] = useState([]);
  const [left, setLeft] = useState([]);
  const [right, setRight] = useState([]);
  const [selFi, setSelFi] = useState(null);
  const [matched, setMatched] = useState(new Set());
  const [wrongId, setWrongId] = useState(null);
  const [time, setTime] = useState(0);
  const [running, setRunning] = useState(false);
  const [done, setDone] = useState(false);
  const timer = useRef(null);

  useEffect(() => {
    api.get("/flashcards", { params: { lang } }).then(({ data }) => setPool(data));
  }, [lang]);

  const startRound = () => {
    if (!pool.length) return;
    const picked = shuffle(pool).slice(0, ROUND);
    setPairs(picked);
    setLeft(shuffle(picked));
    setRight(shuffle(picked));
    setMatched(new Set());
    setSelFi(null);
    setTime(0);
    setDone(false);
    setRunning(true);
  };

  useEffect(() => {
    if (running) {
      timer.current = setInterval(() => setTime((s) => s + 1), 1000);
      return () => clearInterval(timer.current);
    }
  }, [running]);

  useEffect(() => {
    if (pairs.length && matched.size === pairs.length) {
      setRunning(false);
      setDone(true);
    }
  }, [matched, pairs]);

  const pickFi = (item) => {
    if (matched.has(item.id)) return;
    setSelFi(item);
  };

  const pickEn = (item) => {
    if (matched.has(item.id) || !selFi) return;
    if (item.id === selFi.id) {
      setMatched((m) => new Set([...m, item.id]));
      setSelFi(null);
    } else {
      setWrongId(item.id);
      setTimeout(() => setWrongId(null), 500);
      setSelFi(null);
    }
  };

  const mm = String(Math.floor(time / 60)).padStart(2, "0");
  const ss = String(time % 60).padStart(2, "0");

  if (!pairs.length && !done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center">
        <h1 className="font-serif text-3xl font-semibold text-[#0E382B]">{t("hub.match")}</h1>
        <p className="mt-2 text-[#52635B]">{t("hub.matchDesc")}</p>
        <Button onClick={startRound} data-testid="match-start" disabled={!pool.length} className="mt-8 rounded-full h-12 px-8 bg-[#0E382B] hover:bg-[#1C5442] text-white text-base">
          {t("game.start")}
        </Button>
      </div>
    );
  }

  if (done) {
    return (
      <div className="max-w-xl mx-auto px-4 py-20 text-center" data-testid="match-done">
        <Trophy className="w-14 h-14 mx-auto text-[#E46A28]" />
        <h2 className="mt-4 font-serif text-3xl font-semibold text-[#0E382B]">{t("game.greatJob")}</h2>
        <p className="mt-2 text-[#52635B]">{t("game.time")}: {mm}:{ss}</p>
        <Button onClick={startRound} data-testid="match-restart" className="mt-8 rounded-full h-12 px-8 bg-[#0E382B] hover:bg-[#1C5442] text-white">
          <RefreshCw className="w-4 h-4 mr-2" /> {t("game.restart")}
        </Button>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-10 md:py-14">
      <div className="flex items-center justify-between mb-8">
        <h1 className="font-serif text-2xl sm:text-3xl font-semibold text-[#0E382B]">{t("hub.match")}</h1>
        <div className="flex items-center gap-4 text-sm font-medium text-[#52635B]">
          <span data-testid="match-timer">{t("game.time")}: {mm}:{ss}</span>
          <span>{t("game.matched")}: {matched.size}/{pairs.length}</span>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4 sm:gap-6">
        <div className="space-y-3">
          {left.map((item) => {
            const isMatched = matched.has(item.id);
            const isSel = selFi?.id === item.id;
            return (
              <button
                key={item.id}
                onClick={() => pickFi(item)}
                data-testid={`match-fi-${item.id}`}
                disabled={isMatched}
                className={`w-full rounded-2xl px-4 py-3.5 text-left font-medium transition-all border ${
                  isMatched
                    ? "bg-[#E4F0E9] border-[#Bfe0cf] text-[#0E5A35] opacity-60"
                    : isSel
                    ? "bg-[#0E382B] border-[#0E382B] text-white"
                    : "bg-white border-[#E2DFD7] text-[#0E382B] hover:border-[#0E382B]"
                }`}
              >
                <span className="inline-flex items-center gap-2">
                  {item.fi}
                  {!isMatched && (
                    <span onClick={(e) => { e.stopPropagation(); speak(item.fi); }} className="text-[#1E429F]"><Volume2 className="w-4 h-4" /></span>
                  )}
                </span>
              </button>
            );
          })}
        </div>
        <div className="space-y-3">
          {right.map((item) => {
            const isMatched = matched.has(item.id);
            const isWrong = wrongId === item.id;
            return (
              <button
                key={item.id}
                onClick={() => pickEn(item)}
                data-testid={`match-en-${item.id}`}
                disabled={isMatched}
                className={`w-full rounded-2xl px-4 py-3.5 text-left font-medium transition-all border ${
                  isMatched
                    ? "bg-[#E4F0E9] border-[#Bfe0cf] text-[#0E5A35] opacity-60"
                    : isWrong
                    ? "bg-[#FBE3E3] border-[#e7b3b3] text-[#7A2E2E] animate-shake"
                    : "bg-white border-[#E2DFD7] text-[#52635B] hover:border-[#0E382B]"
                }`}
              >
                {item.en}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}
